import i18n from 'i18next';
import type { ResourceKey, ResourceLanguage } from 'i18next';
import { initReactI18next } from 'react-i18next';

const STORAGE_KEY = 'bill-alps-lang';

type SupportedLang = 'fr' | 'de' | 'en' | 'it';

/** Missing keys fall back here, so this language is always loaded. */
const FALLBACK_LANG: SupportedLang = 'fr';

/**
 * One lazy loader per language: each `./locales/<lng>.ts` eagerly globs its own
 * JSON files, so a language is exactly one chunk. A French user never downloads
 * the German/English/Italian strings (~110 KB raw each) — only the active
 * language, plus the fallback, and only before the app renders.
 */
const languageModules = import.meta.glob<{
  default: Record<string, { default: ResourceKey }>;
}>('./locales/*.ts');

const loadedLanguages = new Set<string>();

function isSupportedLang(value: string | null): value is SupportedLang {
  return value === 'fr' || value === 'de' || value === 'en' || value === 'it';
}

function readLangFromQuery(): SupportedLang | null {
  if (typeof window === 'undefined') return null;
  const queryLang = new URLSearchParams(window.location.search).get('lang');
  return isSupportedLang(queryLang) ? queryLang : null;
}

/** `../locales/fr/trial-banner.json` → `trialBanner` (the namespace name). */
function namespaceFromPath(path: string): string {
  const file = path.slice(path.lastIndexOf('/') + 1, -'.json'.length);
  return file.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase());
}

async function loadNamespaces(lng: string): Promise<ResourceLanguage> {
  const load = languageModules[`./locales/${lng}.ts`];
  if (!load) return {};

  const namespaces: ResourceLanguage = {};
  for (const [path, json] of Object.entries((await load()).default)) {
    namespaces[namespaceFromPath(path)] = json.default;
  }
  return namespaces;
}

/**
 * Fetches a language's bundles if they aren't in the store yet. Call before
 * `i18n.changeLanguage` — `useLanguage()` in `./useTranslation` already does.
 */
export async function loadLanguage(lng: string): Promise<void> {
  if (loadedLanguages.has(lng) || !isSupportedLang(lng)) return;
  const namespaces = await loadNamespaces(lng);
  for (const [ns, resources] of Object.entries(namespaces)) {
    i18n.addResourceBundle(lng, ns, resources, true, true);
  }
  loadedLanguages.add(lng);
}

function applyLanguageMeta(lng: string) {
  localStorage.setItem(STORAGE_KEY, lng);
  document.documentElement.lang = lng;
  document.title = i18n.t('pageTitle', { ns: 'common' });
}

/** Awaited by `main.tsx` before the first render, so no key flashes untranslated. */
export async function initI18n(): Promise<typeof i18n> {
  if (i18n.isInitialized) return i18n;

  const queryLang = readLangFromQuery();
  if (queryLang) {
    localStorage.setItem(STORAGE_KEY, queryLang);
  }
  const savedLang = localStorage.getItem(STORAGE_KEY);
  const initialLang =
    queryLang ?? (isSupportedLang(savedLang) ? savedLang : FALLBACK_LANG);

  const languages =
    initialLang === FALLBACK_LANG
      ? [initialLang]
      : [initialLang, FALLBACK_LANG];
  const bundles = await Promise.all(languages.map(loadNamespaces));
  const resources = Object.fromEntries(
    languages.map((lng, index) => [lng, bundles[index]]),
  );
  languages.forEach((lng) => loadedLanguages.add(lng));

  await i18n.use(initReactI18next).init({
    resources,
    lng: initialLang,
    fallbackLng: FALLBACK_LANG,
    defaultNS: 'common',
    interpolation: {
      escapeValue: false,
    },
  });

  i18n.on('languageChanged', applyLanguageMeta);
  applyLanguageMeta(i18n.language);

  return i18n;
}

export default i18n;
